import * as React from 'react';
import Link from 'next/link';
import { FormItem, ModalVerification } from '@/components/dashboard/settings';
import { Template } from './Template';
import { useNotification } from '@/hooks';
import { FieldValues, useForm } from 'react-hook-form';
import { Toggle, Typography } from '@/components/common';
import {
	ClientNotificationsPayload,
	ErrorHttpResponse,
	UserI,
} from '@/interfaces';
import { userService, verificationsService } from '@/api';
import AppRoutes from '@/const/routes';
import { InfoCircle } from '@/public/icons/tsx';
import { formatErrorMessage } from '@/lib';

interface ClientCommunicationPreferencesProps {
	userInformation?: UserI;
	refetch: () => void;
}

export const ClientCommunicationPreferences: React.FC<
	ClientCommunicationPreferencesProps
> = ({ refetch, userInformation }) => {
	const { toastSuccess, toastError } = useNotification();
	const [disabledSMS, setDisabledSMS] = React.useState<boolean>(false);

	const [isVerifyingPhone, setIsVerifyingPhone] =
		React.useState<boolean>(false);
	const [errorVerification, setErrorVerification] = React.useState<
		string | React.ReactNode
	>('');
	const lastTwoDigits = String(userInformation?.phonenumber)?.slice(-2);
	const { register, setValue, handleSubmit, reset, watch, resetField } =
		useForm({
			mode: 'onChange',
		});
	const [initialValues, setInitialValues] = React.useState({
		receive_platform: true,
		receive_sms: false,
		notification_new_messages: true,
		notification_new_cases: true,
	});

	React.useEffect(() => {
		if (userInformation) {
			const initialData = {
				receive_platform: userInformation.notification_by_platform,
				receive_sms: userInformation.notification_by_sms,
				notification_new_messages:
					userInformation.notification_about_new_messages,
				notification_new_cases: userInformation.notification_about_new_cases,
				product_updates_announcements: userInformation.receive_product_updates,
				marketing_emails: userInformation.receive_marketing_emails,
			};
			setInitialValues(initialData);
			reset(initialData); // Establece los valores iniciales en el formulario
		}
		if (userInformation?.is_phone_number_verified) {
			setDisabledSMS(false);
		}
	}, [userInformation]);

	const handleSave = async (data: FieldValues): Promise<void> => {
		try {
			const newData: ClientNotificationsPayload = {
				notificationAboutNewCases: data.notification_new_cases,
				notificationAboutNewMessages: data.notification_new_messages,
				notificationByPlatform: data.receive_platform,
				notificationBySms: data.receive_sms,
				receiveProductUpdates: data.product_updates_announcements,
				receiveMarketingEmails: data.marketing_emails,
			};

			await userService.updateNotificationsPreferences(newData);

			refetch();
			toastSuccess('Your changes have been successfully saved.');
			setDisabledSMS(false);
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const handleCancel = (): void => {
		reset(initialValues);
		setDisabledSMS(false);
	};

	const isDisabledButton = (): void => {
		if (userInformation && !userInformation?.phonenumber) {
			setDisabledSMS(true);

			resetField('receive_sms');
		} else if (userInformation && !userInformation.is_phone_number_verified) {
			setDisabledSMS(true);

			resetField('receive_sms');
		} else {
			setDisabledSMS(false);
		}
	};
	const validatePhoneNumber = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
			setIsVerifyingPhone(true);
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const sentCodeAgain = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const validateOtpCode = async (code: string): Promise<void> => {
		try {
			await verificationsService.validatePhoneOtp(code, 'phone_verification');
			toastSuccess('Your phone number has been successfully verified.');
			refetch();
			setIsVerifyingPhone(false);
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (!status) {
				toastError(formatErrorMessage(undefined));
				setIsVerifyingPhone(false);
			} else if (status === 400) {
				setErrorVerification(
					<span>
						The verification code has expired. <br />
						Please request a new one and try again.
					</span>
				);
			} else {
				setErrorVerification(
					'The verification code is incorrect. Please try again.'
				);
			}
		}
	};
	return (
		<>
			<Template
				title="Customize your notifications preferences"
				onClickSave={handleSubmit(handleSave)}
				onClickCancel={handleCancel}
			>
				<FormItem
					label="Preferred notification method"
					className="!items-start py-6 "
				>
					<div className="flex items-center gap-4 -mt-2">
						{userInformation && (
							<Toggle
								name="receive_email"
								isActive
								register={register}
								setValue={setValue}
								isDisabled
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Email
						</Typography>
					</div>
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="receive_platform"
								isActive={watch('receive_platform')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							App Notifications
						</Typography>
					</div>
					<div>
						<div className="flex  gap-4 mt-5">
							{userInformation && (
								<div onClick={isDisabledButton}>
									<Toggle
										name="receive_sms"
										isActive={watch('receive_sms')}
										register={register}
										setValue={setValue}
										isDisabled={disabledSMS}
									/>
								</div>
							)}
							<div>
								<Typography type="regular-14" className="text-black">
									SMS
								</Typography>
								<Typography type="regular-10" className="max-w-[466px]">
									By enabling SMS notifications, I consent to receive SMS
									messages from ClaimMagic, including updates and promotions.
									Msg & data rates may apply. Reply STOP to unsubscribe.
								</Typography>
							</div>
						</div>
						{disabledSMS && userInformation?.phonenumber && (
							<div className="flex items-center gap-x-1.5 mt-[18.5px]">
								<InfoCircle fill="#FF0043" width="16" height="16" />
								<Typography type="regular-14" className="text-main-red">
									You need to{' '}
									<span
										className="font-semibold text-primary cursor-pointer"
										onClick={validatePhoneNumber}
									>
										Verify your phone
									</span>{' '}
									first.
								</Typography>
							</div>
						)}
						{disabledSMS && !userInformation?.phonenumber && (
							<div className="flex items-center gap-x-1.5 mt-[18.5px]">
								<InfoCircle fill="#FF0043" width="16" height="16" />
								<Typography type="regular-14" className="text-main-red">
									You need to{' '}
									<Link
										className="font-semibold text-primary"
										href={AppRoutes.CLIENTAPP_SETTINGS_PERSONAL}
									>
										add a phone number
									</Link>{' '}
									first.
								</Typography>
							</div>
						)}
					</div>
				</FormItem>
				<FormItem
					label="Types of Communications"
					className="!py-8 !items-start !border-none"
				>
					<div className="flex items-center gap-4 -mt-2">
						{userInformation && (
							<Toggle
								name="case_updates"
								isActive
								register={register}
								setValue={setValue}
								isDisabled
							/>
						)}
						<Typography type="regular-14" className="text-black max-w-[554px]">
							Case Updates: You will receive important case updates via email as
							part of our service. (This cannot be turned off.)
						</Typography>
					</div>
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="notification_new_messages"
								isActive={watch('notification_new_messages')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Email notifications for new messages in your Inbox
						</Typography>
					</div>
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="notification_new_cases"
								isActive={watch('notification_new_cases')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Immediate alerts for new cases that match your profile and
							opportunities to apply
						</Typography>
					</div>
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="product_updates_announcements"
								isActive={watch('product_updates_announcements')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Product updates & announcements
						</Typography>
					</div>
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="marketing_emails"
								isActive={watch('marketing_emails')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Marketing emails
						</Typography>
					</div>
				</FormItem>
			</Template>
			<ModalVerification
				title="Verify your phone"
				description={
					<div>
						<Typography type="regular-16" className="opacity-60">
							{`We've sent a code to your phone number ending in`}
						</Typography>
						<Typography type="regular-16" className="opacity-60">
							{`XXXXXXX${lastTwoDigits}`}
						</Typography>
					</div>
				}
				sentCodeAgain={sentCodeAgain}
				isOpen={isVerifyingPhone}
				setIsOpen={setIsVerifyingPhone}
				setIsModalOpen={setIsVerifyingPhone}
				error={errorVerification}
				setErrorVerification={setErrorVerification}
				validateOtpCode={validateOtpCode}
				numDigits={4}
			/>
		</>
	);
};
